import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import websocketService, { 
  WebSocketStatus, 
  WebSocketMessage, 
  WebSocketConfig 
} from '../lib/websocketService';
import onlineStatusService, { OnlineStatusData } from '../lib/services/onlineStatusService';
import { isLoggedIn, addAuthStatusListener } from '@/lib/auth';

// 将默认pingMessage提取到组件外部，避免重复创建
const DEFAULT_PING_MESSAGE = { type: 'ping', timestamp: 0 };

interface UseWebSocketOptions {
  url?: string;
  autoConnect?: boolean;
  autoReconnect?: boolean;
  reconnectInterval?: number;
  pingInterval?: number;
  pingMessage?: string | object;
  useCustomToken?: boolean;
  customToken?: string;
  debug?: boolean;
  handleOnlineStatus?: boolean; // 是否处理在线状态消息
  onMessage?: (message: WebSocketMessage) => void; // 直接消息处理回调
}

interface UseWebSocketReturn {
  status: WebSocketStatus;
  messages: WebSocketMessage[];
  connect: () => void;
  disconnect: () => void;
  send: (message: string | object) => boolean;
  clearMessages: () => void;
  isConnected: boolean;
  isConnecting: boolean;
  isDisconnected: boolean;
  isError: boolean;
  onlineStatus: OnlineStatusData; // 添加在线状态数据
}

/**
 * React Hook，用于在组件中使用WebSocket
 * 
 * @param options WebSocket配置选项
 * @returns WebSocket状态和控制方法
 */
export const useWebSocket = (options: UseWebSocketOptions = {}): UseWebSocketReturn => {
  // 提取配置选项，设置默认值
  const {
    url = '',
    autoConnect = true,
    autoReconnect = true,
    reconnectInterval = 5000,
    pingInterval = 30000,
    pingMessage = DEFAULT_PING_MESSAGE,
    useCustomToken = false,
    customToken = '',
    debug = true,
    handleOnlineStatus = true, // 默认处理在线状态消息
    onMessage // 直接消息处理回调
  } = options;

  // 状态
  const [status, setStatus] = useState<WebSocketStatus>(websocketService.getStatus());
  const [messages, setMessages] = useState<WebSocketMessage[]>(websocketService.getMessageHistory());
  const [onlineStatus, setOnlineStatus] = useState<OnlineStatusData>(onlineStatusService.getStatus());

  // 计算派生状态
  const isConnected = useMemo(() => status === WebSocketStatus.CONNECTED, [status]);
  const isConnecting = useMemo(() => status === WebSocketStatus.CONNECTING, [status]);
  const isDisconnected = useMemo(() => status === WebSocketStatus.DISCONNECTED, [status]);
  const isError = useMemo(() => status === WebSocketStatus.ERROR, [status]);

  // 使用useMemo缓存配置对象，避免每次渲染都创建新对象
  const config = useMemo((): Partial<WebSocketConfig> => ({
    url,
    autoReconnect,
    reconnectInterval,
    pingInterval,
    pingMessage,
    useCustomToken,
    customToken,
    debug
  }), [url, autoReconnect, reconnectInterval, pingInterval, pingMessage, useCustomToken, customToken, debug]);

  // 初始化WebSocket连接的函数
  const initializeWebSocket = useCallback(() => {
    if (url && isLoggedIn()) {
      websocketService.init(config);
      if (autoConnect) {
        websocketService.connect();
      }
    }
  }, [url, config, autoConnect]);
  
  // 断开WebSocket连接的函数
  const disconnectWebSocket = useCallback(() => {
    websocketService.disconnect();
  }, []);
  
  // 初始化WebSocket服务（仅在组件挂载时执行一次）
  useEffect(() => {
    initializeWebSocket();
    
    // 组件卸载时的清理函数
    return () => {
      // 这里可以根据需要决定是否断开连接
      // websocketService.disconnect();
    };
  }, []); // 空依赖数组，仅在组件挂载时执行
  
  // 监听配置变化，重新初始化WebSocket
  useEffect(() => {
    if (url && isLoggedIn()) {
      websocketService.init(config);
      // 如果当前未连接且设置了自动连接，则连接
      if (autoConnect && websocketService.getStatus() === WebSocketStatus.DISCONNECTED) {
        websocketService.connect();
      }
    }
  }, [config, autoConnect, url]);
  
  // 监听登录状态变化
  useEffect(() => {
    const unsubscribe = addAuthStatusListener((loggedIn: boolean) => {
      if (!url) return;
      
      if (loggedIn) {
        // 用户登录，初始化并连接WebSocket
        initializeWebSocket();
      } else {
        // 用户登出，断开WebSocket连接
        disconnectWebSocket();
      }
    });
    
    return unsubscribe;
  }, [url, initializeWebSocket, disconnectWebSocket]);

  // 监听WebSocket状态变化
  useEffect(() => {
    const unsubscribe = websocketService.addStatusListener(setStatus);
    return unsubscribe;
  }, []);

  // 监听WebSocket消息
  useEffect(() => {
    const handleMessage = (message: WebSocketMessage) => {
      // 如果提供了自定义消息处理回调，优先使用
      if (onMessage) {
        onMessage(message);
        return;
      }
      
      // 如果是清除消息的信号，则清空消息列表
      if (message.content === 'history-cleared') {
        setMessages([]);
        return;
      }
      
      // 处理在线状态消息
      if (handleOnlineStatus && 
          message.type === 'received' && 
          message.content?.type === 'online_status' && 
          message.content?.data) {
        // 更新全局在线状态服务
        onlineStatusService.updateStatus(message.content.data);
      }
      
      // 添加消息到列表
      setMessages(prev => [...prev, message]);
    };
    
    const unsubscribe = websocketService.addMessageListener(handleMessage);
    return unsubscribe;
  }, [handleOnlineStatus, onMessage]);

  // 订阅在线状态更新
  useEffect(() => {
    const unsubscribe = onlineStatusService.subscribeToStatusUpdates(setOnlineStatus);
    return unsubscribe;
  }, []);

  // 连接WebSocket
  const connect = useCallback(() => {
    websocketService.connect();
  }, []);

  // 断开WebSocket连接
  const disconnect = useCallback(() => {
    websocketService.disconnect();
  }, []);

  // 发送消息
  const send = useCallback((message: string | object): boolean => {
    return websocketService.send(message);
  }, []);

  // 清除消息历史
  const clearMessages = useCallback(() => {
    websocketService.clearMessageHistory();
  }, []);

  return {
    status,
    messages,
    connect,
    disconnect,
    send,
    clearMessages,
    isConnected,
    isConnecting,
    isDisconnected,
    isError,
    onlineStatus // 返回在线状态数据
  };
};

export default useWebSocket;